import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  FEATURE_HINT_TARGET_ATTR,
  RING_CLASSNAME,
} from './feature-hints.constants';
import {
  getActiveFeatureHint,
  getFeatureHintTarget,
  getFeatureHintTargetsVersion,
  getRegisteredFeatureHintIds,
  hasFeatureHintTarget,
  registerFeatureHintTarget,
  setActiveFeatureHint,
  subscribeFeatureHints,
} from './feature-hints-store';
import type { FeatureHintStep } from './feature-hints.types';
import FeatureHintsTooltip from './feature-hints-tooltip';

export type FeatureHintDismissReason = 'skip' | 'escape';

export interface FeatureHintsProviderProps {
  steps: FeatureHintStep[];
  startEventName?: string;
  skipMissingTargets?: boolean;
  onStepViewed?: (step: FeatureHintStep, stepIndex: number) => void;
  onTourDismissed?: (
    step: FeatureHintStep,
    stepIndex: number,
    reason: FeatureHintDismissReason
  ) => void;
  onTourCompleted?: (step: FeatureHintStep, stepIndex: number) => void;
  children: ReactNode;
}

interface FeatureHintsActiveState {
  id: string;
  position: number;
}

interface FeatureHintsContextValue {
  steps: FeatureHintStep[];
  availableSteps: FeatureHintStep[];
  skipMissingTargets: boolean;
  active: FeatureHintsActiveState | null;
  setActive: Dispatch<SetStateAction<FeatureHintsActiveState | null>>;
  notifyTourDismissed: (
    step: FeatureHintStep,
    stepIndex: number,
    reason: FeatureHintDismissReason
  ) => void;
  notifyTourCompleted: (step: FeatureHintStep, stepIndex: number) => void;
}

export interface UseFeatureHintsResult {
  activeId: string | null;
  activeStep: FeatureHintStep | null;
  currentStep: number;
  totalSteps: number;
  isLast: boolean;
  steps: FeatureHintStep[];
  start: () => boolean;
  next: () => void;
  close: () => void;
}

export type FeatureHintTargetProps = {
  [FEATURE_HINT_TARGET_ATTR]: string;
  className?: string;
  ref: (element: HTMLElement | null) => void;
};

const FeatureHintsContext = createContext<FeatureHintsContextValue | null>(
  null
);

const INACTIVE_HINT_SNAPSHOT = '';

const getActiveHintSnapshot = (id: string): string => {
  const activeHint = getActiveFeatureHint();

  return activeHint && activeHint.id === id
    ? `1:${activeHint.ringClassName ?? ''}`
    : INACTIVE_HINT_SNAPSHOT;
};

const useFeatureHintActiveSnapshot = (id: string): string =>
  useSyncExternalStore(
    subscribeFeatureHints,
    () => getActiveHintSnapshot(id),
    () => getActiveHintSnapshot(id)
  );

const useFeatureHintTargetsVersion = (): number =>
  useSyncExternalStore(
    subscribeFeatureHints,
    getFeatureHintTargetsVersion,
    getFeatureHintTargetsVersion
  );

export const useIsFeatureHintActive = (id: string): boolean =>
  useFeatureHintActiveSnapshot(id) !== INACTIVE_HINT_SNAPSHOT;

const isAnyFeatureHintActive = (): boolean => getActiveFeatureHint() !== null;

export const useIsAnyFeatureHintActive = (): boolean =>
  useSyncExternalStore(
    subscribeFeatureHints,
    isAnyFeatureHintActive,
    isAnyFeatureHintActive
  );

export const useFeatureHintTarget = (id: string): FeatureHintTargetProps => {
  const snapshot = useFeatureHintActiveSnapshot(id);
  const registeredElementRef = useRef<HTMLElement | null>(null);

  const ref = useCallback(
    (element: HTMLElement | null) => {
      if (element) {
        registeredElementRef.current = element;
        registerFeatureHintTarget(id, element);

        return;
      }

      if (getFeatureHintTarget(id) === registeredElementRef.current) {
        registerFeatureHintTarget(id, null);
      }

      registeredElementRef.current = null;
    },
    [id]
  );

  return useMemo(() => {
    if (snapshot === INACTIVE_HINT_SNAPSHOT) {
      return {
        [FEATURE_HINT_TARGET_ATTR]: id,
        ref,
      };
    }

    return {
      [FEATURE_HINT_TARGET_ATTR]: id,
      className: twMerge(RING_CLASSNAME, snapshot.slice(2)),
      ref,
    };
  }, [id, snapshot, ref]);
};

const useAvailableFeatureHintSteps = (
  steps: FeatureHintStep[],
  skipMissingTargets: boolean
): FeatureHintStep[] => {
  const targetsVersion = useFeatureHintTargetsVersion();
  const registeredIds = useMemo(
    () => getRegisteredFeatureHintIds(),
    [targetsVersion]
  );

  return useMemo(() => {
    if (!skipMissingTargets) {
      return steps;
    }

    return steps.filter((step) => registeredIds.has(step.id));
  }, [steps, skipMissingTargets, registeredIds]);
};

const isFeatureHintStepAvailable = (
  id: string,
  skipMissingTargets: boolean
): boolean => !skipMissingTargets || hasFeatureHintTarget(id);

interface FeatureHintStepMatch {
  step: FeatureHintStep;
  configuredIndex: number;
}

const findNextAvailableFeatureHintStep = (
  steps: FeatureHintStep[],
  fromIndex: number,
  skipMissingTargets: boolean
): FeatureHintStepMatch | null => {
  for (let index = fromIndex; index < steps.length; index += 1) {
    if (isFeatureHintStepAvailable(steps[index].id, skipMissingTargets)) {
      return { step: steps[index], configuredIndex: index };
    }
  }

  return null;
};

export const useFeatureHints = (): UseFeatureHintsResult => {
  const context = useContext(FeatureHintsContext);

  if (!context) {
    throw new Error(
      'useFeatureHints must be used within a FeatureHintsProvider'
    );
  }

  const {
    steps,
    availableSteps,
    skipMissingTargets,
    active,
    setActive,
    notifyTourDismissed,
    notifyTourCompleted,
  } = context;

  const activeConfiguredIndex =
    active === null ? -1 : steps.findIndex((step) => step.id === active.id);
  const activeStep =
    activeConfiguredIndex === -1 ? null : steps[activeConfiguredIndex];

  const totalSteps = availableSteps.length;
  const currentStep = active === null ? 0 : active.position;
  const isLast =
    active !== null &&
    activeConfiguredIndex !== -1 &&
    findNextAvailableFeatureHintStep(
      steps,
      activeConfiguredIndex + 1,
      skipMissingTargets
    ) === null;

  const start = useCallback((): boolean => {
    const match = findNextAvailableFeatureHintStep(
      steps,
      0,
      skipMissingTargets
    );

    if (!match) {
      return false;
    }

    setActive({ id: match.step.id, position: 1 });

    return true;
  }, [steps, skipMissingTargets, setActive]);

  const close = useCallback(() => {
    if (active !== null) {
      const configuredIndex = steps.findIndex((step) => step.id === active.id);
      const step = configuredIndex === -1 ? null : steps[configuredIndex];

      if (step) {
        notifyTourDismissed(step, configuredIndex, 'skip');
      }
    }

    setActive(null);
  }, [active, steps, notifyTourDismissed, setActive]);

  const next = useCallback(() => {
    if (active === null) {
      return;
    }

    const configuredIndex = steps.findIndex((step) => step.id === active.id);

    if (configuredIndex === -1) {
      setActive(null);

      return;
    }

    const match = findNextAvailableFeatureHintStep(
      steps,
      configuredIndex + 1,
      skipMissingTargets
    );

    if (!match) {
      notifyTourCompleted(steps[configuredIndex], configuredIndex);
      setActive(null);

      return;
    }

    setActive({ id: match.step.id, position: active.position + 1 });
  }, [active, steps, skipMissingTargets, notifyTourCompleted, setActive]);

  return {
    activeId: activeStep?.id ?? null,
    activeStep,
    currentStep,
    totalSteps,
    isLast,
    steps: availableSteps,
    start,
    next,
    close,
  };
};

export const FeatureHintsProvider = ({
  steps,
  startEventName,
  skipMissingTargets = false,
  onStepViewed,
  onTourDismissed,
  onTourCompleted,
  children,
}: FeatureHintsProviderProps) => {
  const [active, setActive] = useState<FeatureHintsActiveState | null>(null);
  const availableSteps = useAvailableFeatureHintSteps(
    steps,
    skipMissingTargets
  );
  const targetsVersion = useFeatureHintTargetsVersion();

  const activeConfiguredIndex =
    active === null ? -1 : steps.findIndex((step) => step.id === active.id);
  const activeStep =
    activeConfiguredIndex === -1 ? null : steps[activeConfiguredIndex];

  const onStepViewedRef = useRef(onStepViewed);
  const onTourDismissedRef = useRef(onTourDismissed);
  const onTourCompletedRef = useRef(onTourCompleted);

  useEffect(() => {
    onStepViewedRef.current = onStepViewed;
    onTourDismissedRef.current = onTourDismissed;
    onTourCompletedRef.current = onTourCompleted;
  });

  const notifyStepViewed = useCallback(
    (step: FeatureHintStep, stepIndex: number) => {
      onStepViewedRef.current?.(step, stepIndex);
    },
    []
  );

  const notifyTourDismissed = useCallback(
    (
      step: FeatureHintStep,
      stepIndex: number,
      reason: FeatureHintDismissReason
    ) => {
      onTourDismissedRef.current?.(step, stepIndex, reason);
    },
    []
  );

  const notifyTourCompleted = useCallback(
    (step: FeatureHintStep, stepIndex: number) => {
      onTourCompletedRef.current?.(step, stepIndex);
    },
    []
  );

  useEffect(() => {
    if (!startEventName) {
      return undefined;
    }

    const handleStart = () => {
      const match = findNextAvailableFeatureHintStep(
        steps,
        0,
        skipMissingTargets
      );

      if (match) {
        setActive({ id: match.step.id, position: 1 });
      }
    };

    window.addEventListener(startEventName, handleStart);

    return () => {
      window.removeEventListener(startEventName, handleStart);
    };
  }, [startEventName, steps, skipMissingTargets]);

  useLayoutEffect(() => {
    if (active === null) {
      return;
    }

    if (activeConfiguredIndex === -1) {
      setActive(null);

      return;
    }

    if (isFeatureHintStepAvailable(active.id, skipMissingTargets)) {
      return;
    }

    const match = findNextAvailableFeatureHintStep(
      steps,
      activeConfiguredIndex + 1,
      skipMissingTargets
    );

    setActive(
      match ? { id: match.step.id, position: active.position + 1 } : null
    );
  }, [
    active,
    activeConfiguredIndex,
    steps,
    skipMissingTargets,
    targetsVersion,
  ]);

  const lastViewedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeStep) {
      lastViewedIdRef.current = null;

      return;
    }

    if (lastViewedIdRef.current === activeStep.id) {
      return;
    }

    lastViewedIdRef.current = activeStep.id;
    notifyStepViewed(activeStep, activeConfiguredIndex);
  }, [activeStep, activeConfiguredIndex, notifyStepViewed]);

  useEffect(() => {
    if (!activeStep) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        notifyTourDismissed(activeStep, activeConfiguredIndex, 'escape');
        setActive(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeStep, activeConfiguredIndex, notifyTourDismissed]);

  useLayoutEffect(() => {
    if (!activeStep) {
      return undefined;
    }

    setActiveFeatureHint({
      id: activeStep.id,
      ringClassName: activeStep.ringClassName,
    });

    return () => {
      if (getActiveFeatureHint()?.id === activeStep.id) {
        setActiveFeatureHint(null);
      }
    };
  }, [activeStep]);

  const scrolledStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeStep) {
      scrolledStepIdRef.current = null;

      return;
    }

    if (scrolledStepIdRef.current === activeStep.id) {
      return;
    }

    const element = getFeatureHintTarget(activeStep.id);

    if (!element) {
      return;
    }

    scrolledStepIdRef.current = activeStep.id;
    element.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeStep, targetsVersion]);

  const value = useMemo(
    () => ({
      steps,
      availableSteps,
      skipMissingTargets,
      active,
      setActive,
      notifyTourDismissed,
      notifyTourCompleted,
    }),
    [
      steps,
      availableSteps,
      skipMissingTargets,
      active,
      notifyTourDismissed,
      notifyTourCompleted,
    ]
  );

  return (
    <FeatureHintsContext.Provider value={value}>
      {children}
      <FeatureHintsTooltip />
    </FeatureHintsContext.Provider>
  );
};
